import {Module} from 'vuex';
import {RootState} from '@/store';
import {fetchCreate, fetchDelete, fetchDeleteAll, fetchDownload, fetchPlan} from '@/api/journals/plan';
import PlanModel, {DialogData} from '@/models/PlanModel';
import {PAGINATION_ROW_COUNT_SIZES} from '@/configs/constants';

type PlanState = {
    loading: boolean;
    plan: null | PlanModel;
    currentPageIndex: number;
    currentRowCount: number;
};

const PlanModule: Module<PlanState, RootState> = {
    namespaced: true,
    state: {
        loading: false,
        plan: null,
        currentPageIndex: 1,
        currentRowCount: PAGINATION_ROW_COUNT_SIZES[0],
    },
    getters: {
        pagination: state => state.plan?.Paging,
        items: state => state.plan?.Items,
    },
    mutations: {
        setLoading: (state, bool) => (state.loading = bool),
        setPlan: (state, data) => (state.plan = data),
        setCurrentPageIndex: (state, index) => (state.currentPageIndex = index),
        setCurrentRowCount: (state, count) => (state.currentRowCount = count),
    },
    actions: {
        getData: ({commit, state}, {pageIndex, rowCount}) => {
            if (state.loading) return;
            return new Promise(async (resolve, reject) => {
                commit('setLoading', true);
                commit('setCurrentPageIndex', pageIndex);
                commit('setCurrentRowCount', rowCount);
                try {
                    const {data} = await fetchPlan(pageIndex, rowCount);
                    commit('setPlan', data);
                    commit('setCurrentPageIndex', data.Paging.PageIndex);
                    resolve();
                } finally {
                    commit('setLoading', false);
                }
            });
        },
        create: ({commit, state}) => {
            if (state.loading) return;
            return new Promise<DialogData>(async (resolve, reject) => {
                commit('setLoading', true);
                try {
                    const {data} = await fetchCreate();
                    resolve(data);
                } catch (e) {
                    reject(e);
                } finally {
                    commit('setLoading', false);
                }
            });
        },
        download: ({commit, state}, id: number) => {
            if (state.loading) return;
            return new Promise(async (resolve, reject) => {
                commit('setLoading', true);
                try {
                    const response = await fetchDownload(id);
                    resolve(response);
                } catch (e) {
                    reject(e);
                } finally {
                    commit('setLoading', false);
                }
            });
        },
        delete: ({commit, state}, {id, page, size}) => {
            if (state.loading) return;
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchDelete(id, page, size);
                    if (data.Dependencies) resolve(data.Dependencies);
                    else {
                        commit('setPlan', data);
                        resolve(null);
                    }
                } catch (e) {
                    reject(e);
                }
            });
        },
        deleteAll: ({commit, state}) => {
            if (state.loading) return;
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchDeleteAll();
                    if (data.Dependencies) resolve(data.Dependencies);
                    else {
                        commit('setPlan', data);
                        resolve(null);
                    }
                } catch (e) {
                    reject(e);
                }
            });
        },
    },
};

export default PlanModule;
